import { useState } from 'react';
import * as XLSX from 'xlsx';
import { parseCSV } from '../utils/csv';
import { prequalificationService, contactService, userService, tendersService, contractsService } from '../services/api';

// Maximum CSV / Excel file size (10 MB)
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const INITIAL_PROGRESS = {
  open: false,
  total: 0,
  current: 0,
  successCount: 0,
  failedRows: [],
  done: false,
  unmatchedSources: [],
};

export function useCsvUpload(importTypeOrSources = 'prequalifications', onImportDone = null) {
  const importType = typeof importTypeOrSources === 'string' ? importTypeOrSources : 'prequalifications';
  const [importProgress, setImportProgress] = useState(INITIAL_PROGRESS);

  const closeImportModal = () => {
    setImportProgress(INITIAL_PROGRESS);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ── FILE SIZE GUARD ──────────────────────────────────────────────────────
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setImportProgress({
        ...INITIAL_PROGRESS,
        open: true,
        done: true,
        importError: `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed is 10 MB.`,
      });
      e.target.value = '';
      return;
    }

    const processRows = async (rows) => {
      try {
        if (!rows || rows.length === 0) {
          setImportProgress({
            ...INITIAL_PROGRESS,
            open: true,
            done: true,
            importError: 'The selected file is empty.',
          });
          return;
        }

        const findHeaderIndex = (rawRows, type) => {
          let bestIndex = -1;
          let bestScore = -1;

          const leadKeywords = [
            'client', 'name', 'customer', 'prequalification', 'title',
            'phone', 'contact', 'mobile', 'cell', 'whatsapp',
            'email', 'mail',
            'status', 'stage',
            'source',
            'manager', 'assignee', 'sales',
            'inquiry', 'date', 'created',
            'event', 'function', 'venue', 'guests',
            'amount', 'quotation', 'estimate', 'budget',
            'notes', 'visited', 'follow',
            'services', 'service', 'submission', 'enlisted'
          ];
          const contactKeywords = [
            'first', 'last', 'name', 'phone', 'contact', 'mobile', 'email', 'company', 'address'
          ];
          const userKeywords = [
            'name', 'email', 'role', 'parent', 'report'
          ];
          const tenderKeywords = [
            'tender', 'title', 'proposal', 'value', 'amount', 'earnest', 'submission', 'status', 'client',
            'services', 'service', 'duration', 'award', 'awarded'
          ];
          const profileKeywords = [
            'client', 'name', 'customer', 'person', 'project', 'title',
            'mobile', 'phone', 'cell', 'tel', 'whatsapp', 'contact',
            'email', 'mail',
            'designation', 'role', 'address', 'city', 'location',
            'company', 'source', 'status', 'stage',
            'manager', 'assignee', 'sales',
            'notes', 'remarks', 'comment', 'follow', 'date', 'created', 'ext',
            'services', 'service'
          ];
          const contractKeywords = [
            'contract', 'order', 'agreement', 'title', 'client', 'name', 'phone', 'mobile', 'value', 'amount', 'start', 'expiry', 'end', 'duration', 'status', 'collected',
            'services', 'service'
          ];

          const keywords = type === 'contacts'
            ? contactKeywords
            : (type === 'users'
              ? userKeywords
              : (type === 'tenders'
                ? tenderKeywords
                : (type === 'profiles'
                  ? profileKeywords
                  : (type === 'contracts'
                    ? contractKeywords
                    : leadKeywords))));

          const hasIdentifier = (clean) => {
            return [
              'name', 'client', 'customer', 'phone', 'contact', 'mobile',
              'cell', 'tel', 'whatsapp', 'email', 'mail', 'person', 'company',
              'lead', 'title', 'project', 'user', 'role', 'designation', 'date'
            ].some(k => clean.includes(k));
          };

          const limit = Math.min(rawRows.length, 25);
          for (let i = 0; i < limit; i++) {
            const row = rawRows[i];
            if (!Array.isArray(row) || row.length < 2) continue;
            let score = 0;
            let hasId = false;

            for (const cell of row) {
              if (cell === null || cell === undefined) continue;
              const clean = String(cell).replace(/"/g, '').trim().toLowerCase();
              if (!clean) continue;
              if (keywords.some(kw => clean.includes(kw))) {
                score++;
              }
              if (hasIdentifier(clean)) {
                hasId = true;
              }
            }

            if (hasId && score > bestScore) {
              bestScore = score;
              bestIndex = i;
            }
          }

          // Fallback: If heuristic did not trigger, pick the first row with at least 2 non-empty columns
          if (bestIndex === -1) {
            for (let i = 0; i < Math.min(rawRows.length, 10); i++) {
              const row = rawRows[i];
              if (Array.isArray(row) && row.filter(c => String(c ?? '').trim().length > 0).length >= 2) {
                return i;
              }
            }
          }

          return bestIndex;
        };

        const headerIndex = findHeaderIndex(rows, importType);

        if (headerIndex === -1) {
          setImportProgress({
            ...INITIAL_PROGRESS,
            open: true,
            done: true,
            importError: 'Invalid CSV / Excel file: Could not find required column headers.',
          });
          return;
        }

        const cleanHeaderStr = (str) => String(str ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const headers = rows[headerIndex].map((h) => String(h ?? '').replace(/"/g, '').trim().toLowerCase());
        const cleanedHeaders = headers.map(cleanHeaderStr);

        const get = (row, ...keys) => {
          for (const k of keys) {
            const rawK = String(k).trim().toLowerCase();
            const cleanK = cleanHeaderStr(k);
            if (!cleanK) continue;

            // 1. Exact match on raw header
            let idx = headers.findIndex((h) => h === rawK);
            // 2. Exact match on cleaned header (ignores spaces, hyphens, slashes, etc.)
            if (idx === -1) idx = cleanedHeaders.findIndex((ch) => ch === cleanK);
            // 3. Substring match on raw header
            if (idx === -1) idx = headers.findIndex((h) => h.includes(rawK));
            // 4. Substring match on cleaned header
            if (idx === -1) idx = cleanedHeaders.findIndex((ch) => ch.includes(cleanK));

            if (idx > -1 && row[idx] !== undefined && row[idx] !== null) {
              const val = String(row[idx]).replace(/"/g, '').trim();
              if (val !== '') return val;
            }
          }
          return '';
        };

        const dataToImport = [];

        /**
         * Excel exports comma-formatted numbers WITHOUT quotes in CSV.
         * e.g. 1,450,000 → ["1", "450", "000"] (3 separate cells).
         * Re-merge split cells back into one cell if needed.
         */
        const normalizeRow = (row) => {
          const amtIdx = headers.findIndex(
            (h) => h === 'amount' || h === 'quotation amount' || h.startsWith('amount') || h.includes('estimate') || h.includes('total value') || h.includes('contract value') || h.includes('collected')
          );
          if (amtIdx < 0 || amtIdx >= row.length) return row;

          let j = amtIdx + 1;
          while (j < row.length && /^\d{3}$/.test(String(row[j] || '').trim())) j++;

          if (j === amtIdx + 1) return row;

          const merged = row.slice(amtIdx, j).join('');
          return [
            ...row.slice(0, amtIdx),
            merged,
            ...row.slice(j)
          ];
        };

        const cleanNumber = (val) => {
          if (!val) return '';
          return String(val).replace(/[^\d.]/g, '');
        };

        rows.slice(headerIndex + 1).forEach((row, originalRowIndex) => {
          if (!row || row.length < 2) {
            return;
          }

          const isRowEmpty = row.every(cell => {
            if (cell === null || cell === undefined) return true;
            return String(cell).replace(/[\s\uFEFF\xA0"']/g, '') === '';
          });
          if (isRowEmpty) {
            return;
          }

          const r = normalizeRow(row);

          if (importType === 'contacts') {
            const firstName = get(r, 'first name', 'firstname', 'name');
            const lastName = get(r, 'last name', 'lastname');
            const phoneStr = get(r, 'mobile', 'phone', 'cell', 'contact');
            const emailStr = get(r, 'email', 'mail');

            const isNameEmpty = !firstName || String(firstName).replace(/[\s\uFEFF\xA0"']/g, '') === '';
            const isPhoneEmpty = !phoneStr || String(phoneStr).replace(/[\s\uFEFF\xA0"']/g, '') === '';

            if (isNameEmpty && isPhoneEmpty) return;

            dataToImport.push({
              firstName: firstName || 'Unknown',
              lastName: lastName || null,
              phone: phoneStr,
              email: emailStr || null
            });
          } else if (importType === 'users') {
            const name = get(r, 'name', 'user name', 'full name');
            const email = get(r, 'email', 'mail', 'email address');
            const role = get(r, 'role', 'user role', 'position');
            const parentEmail = get(r, 'parent email', 'reports to', 'report to', 'parent', 'manager email');

            if (!name || !email) {
              console.warn(`Skipping row ${headerIndex + 1 + originalRowIndex + 1}: Name or email missing.`, row);
              return;
            }

            dataToImport.push({
              name,
              email,
              role,
              parentEmail
            });
          } else if (importType === 'tenders') {
            const clientName = get(r, 'client name', 'clientname', 'client', 'customer name', 'customer', 'company name', 'company', 'organization', 'name');
            const title = get(r, 'tender title', 'title', 'tender', 'project name', 'project', 'proposal title', 'proposal');
            const mobile = get(r, 'mobile', 'phone', 'contact number', 'mobile number', 'cell', 'cell no', 'phone number', 'whatsapp', 'tel');

            if ((!clientName || String(clientName).trim() === '') && (!title || String(title).trim() === '')) {
              console.warn(`Skipping row ${headerIndex + 1 + originalRowIndex + 1}: Missing title and client.`);
              return;
            }

            const rawStatus = get(r, 'status', 'stage', 'award status', 'award', 'tender status');
            // In tenders, awarded/award means Won
            const status = (rawStatus && (rawStatus.toLowerCase().includes('award') || rawStatus.toLowerCase().includes('won'))) ? 'Won' : (rawStatus || 'In Process');

            dataToImport.push({
              title: title,
              clientName: clientName,
              contactPersonName: get(r, 'contact person name', 'contact person', 'person name', 'person', 'poc', 'focal person'),
              designation: get(r, 'designation', 'role', 'title', 'position'),
              mobile: mobile,
              email: get(r, 'email', 'mail', 'email address', 'e-mail'),
              city: get(r, 'city', 'location'),
              address: get(r, 'address'),
              type: get(r, 'tender type', 'type', 'proposal type', 'category'),
              status: status,
              proposalValue: cleanNumber(get(r, 'proposal value', 'value', 'amount', 'quotation amount', 'contract value', 'bid value', 'total value', 'quoted amount')),
              negotiation: cleanNumber(get(r, 'negotiated amount', 'negotiation', 'negotiated value')),
              grossProfit: cleanNumber(get(r, 'gross profit', 'gp', 'profit')),
              wht: cleanNumber(get(r, 'wht', 'withholding tax', 'tax')),
              mobilizationAdvance: cleanNumber(get(r, 'mobilization advance', 'advance', 'mobilization')),
              contractDuration: get(r, 'contract end date / duration', 'contract end date', 'contract duration', 'duration', 'contract period', 'tenure', 'period'),
              noOfStaff: cleanNumber(get(r, 'no of staff', 'number of staff', 'staff count', 'staff', 'headcount')),
              earnestMoney: cleanNumber(get(r, 'earnest money value', 'earnest money', 'earnest', 'bid security', 'security deposit')),
              paymentTerms: get(r, 'payment terms', 'terms', 'payment schedule'),
              source: get(r, 'source', 'lead source', 'channel'),
              manager: get(r, 'assignee', 'manager', 'assigned to', 'assignedto', 'salesperson', 'sales person', 'sales', 'owner'),
              approvalStatus: get(r, 'approval status', 'approval', 'approvalstatus'),
              approvalRemarks: get(r, 'approval remarks', 'approval comments', 'approval note', 'approval remarks / comments'),
              createdDate: get(r, 'created date', 'date', 'created on', 'inquiry date', 'tender date', 'rfp date', 'date created', 'created at'),
              submissionDate: get(r, 'submission date', 'date of submission', 'submission', 'tender submission date', 'bid submission date', 'closing date', 'due date'),
              services: get(r, 'services', 'service', 'scope of work', 'scope', 'services required', 'scope of service', 'nature of work'),
              notes: get(r, 'notes', 'remarks', 'comments', 'comment', 'description'),
              awardedOn: get(r, 'awarded on', 'award date', 'date awarded', 'won date', 'date won'),
              followUpDate: get(r, 'follow up date', 'follow up', 'follow-up', 'date for follow-up', 'next follow up', 'next call date')
            });
          } else if (importType === 'profiles') {
            const clientName = get(r, 'client name', 'clientname', 'client', 'customer name', 'customer', 'company name', 'company', 'organization', 'name');
            const contactPersonName = get(r, 'contact person name', 'contact person', 'person name', 'person', 'poc', 'focal person');
            const mobile = get(r, 'mobile', 'phone', 'contact number', 'mobile number', 'cell', 'cell #', 'cell no', 'contact no', 'phone number', 'whatsapp', 'tel');
            const email = get(r, 'email', 'mail', 'email address', 'e-mail');
            const projectName = get(r, 'project name', 'project', 'title');

            if (!clientName && !mobile && !contactPersonName && !email && !projectName) {
              console.warn(`Skipping row ${headerIndex + 1 + originalRowIndex + 1}: Missing client name, contact, mobile, email, and project.`);
              return;
            }

            dataToImport.push({
              date: get(r, 'created date', 'date', 'created on', 'inquiry date', 'created at', 'date created', 'entry date'),
              projectName: projectName,
              clientName: clientName || contactPersonName || projectName || 'Unknown Client',
              contactPersonName: contactPersonName,
              designation: get(r, 'designation', 'role', 'title', 'position'),
              contactDetailExt: get(r, 'contact detail ext', 'contact detail', 'ext', 'extension', 'landline', 'contact ext'),
              mobile: mobile,
              email: email,
              city: get(r, 'city', 'location'),
              address: get(r, 'address'),
              source: get(r, 'source', 'channel', 'medium', 'lead source'),
              status: get(r, 'status', 'stage'),
              manager: get(r, 'assignee', 'manager', 'assigned to', 'assignedto', 'salesperson', 'employee', 'sales', 'owner'),
              approvalStatus: get(r, 'approval status', 'approval', 'approvalstatus'),
              approvalRemarks: get(r, 'approval remarks', 'approval comments', 'approval note', 'approval remarks / comments'),
              notes: get(r, 'notes', 'remarks', 'comment', 'comments', 'description'),
              followUpDate: get(r, 'follow up date', 'follow up', 'follow-up', 'date for follow-up', 'next call date', 'next follow up'),
              nextCallDate: get(r, 'next call date', 'follow up date', 'follow up', 'follow-up', 'date for follow-up', 'next follow up')
            });
          } else if (importType === 'contracts') {
            const clientName = get(r, 'client name', 'clientname', 'client', 'customer name', 'customer', 'company name', 'company', 'organization', 'name');
            const title = get(r, 'project / title', 'project name', 'project', 'title', 'contract title');
            const contractNumber = get(r, 'contract #', 'contract no', 'contract number', 'contract id', 'order #', 'order no', 'id');
            const mobile = get(r, 'phone', 'mobile', 'contact number', 'mobile number', 'cell', 'tel', 'whatsapp');

            if (!clientName && !title && !contractNumber && !mobile) {
              console.warn(`Skipping row ${headerIndex + 1 + originalRowIndex + 1}: Missing client, title, and contract number.`);
              return;
            }

            dataToImport.push({
              contractNumber: contractNumber || null,
              clientName: clientName || null,
              mobile: mobile || null,
              email: get(r, 'email', 'mail', 'email address', 'e-mail'),
              contactPersonName: get(r, 'contact person name', 'contact person', 'person', 'poc', 'focal person'),
              designation: get(r, 'designation', 'role', 'title', 'position'),
              city: get(r, 'city', 'location'),
              address: get(r, 'address'),
              title: title || clientName || 'Contract',
              contractType: get(r, 'service type', 'contract type', 'services', 'service', 'scope of work', 'scope', 'type'),
              startDate: get(r, 'start date', 'contract date', 'commencement date', 'effective date', 'date'),
              endDate: get(r, 'end date', 'expiry date', 'expiry', 'expiration date', 'contract end date'),
              contractDuration: get(r, 'contract duration', 'duration', 'period', 'contract period', 'tenure'),
              contractValue: cleanNumber(get(r, 'total value', 'contract value', 'value', 'amount', 'total amount')),
              paidAmount: cleanNumber(get(r, 'collected', 'paid amount', 'paid', 'received', 'received amount')),
              paymentStatus: get(r, 'payment status', 'payment'),
              paymentTerms: get(r, 'payment terms', 'terms', 'payment schedule'),
              status: get(r, 'status', 'contract status'),
              manager: get(r, 'assignee', 'manager', 'assigned to', 'salesperson', 'owner'),
              remarks: get(r, 'remarks', 'notes', 'comments', 'comment')
            });
          } else {
            const name = get(r, 'client name', 'clientname', 'client', 'customer name', 'customer', 'prequalification name', 'prequalification', 'company name', 'company', 'organization', 'title', 'name', 'person');
            const phone = get(r, 'mobile', 'phone', 'contact number', 'mobile number', 'phone number', 'cell', 'cell #', 'cell no', 'tel', 'whatsapp', 'number', 'contact');

            const isNameEmpty = !name || String(name).replace(/[\s\uFEFF\xA0"']/g, '') === '';
            const isPhoneEmpty = !phone || String(phone).replace(/[\s\uFEFF\xA0"']/g, '') === '';

            if (isNameEmpty && isPhoneEmpty) {
              console.warn(`Skipping row ${headerIndex + 1 + originalRowIndex + 1}: No client name or phone number found.`, row);
              return;
            }

            const notesVal = get(r, 'notes', 'remarks', 'comments', 'comment', 'description');
            const visitedVal = get(r, 'visited', 'site visited');
            const followUpVal = get(r, 'date for follow-up', 'follow up', 'follow-up', 'follow up date', 'next call date', 'next follow up');
            const isSiteVisited = visitedVal && visitedVal.toLowerCase().trim() === 'yes';

            let combinedNotes = notesVal;
            if (visitedVal) combinedNotes = combinedNotes ? `${combinedNotes} | Visited: ${visitedVal}` : `Visited: ${visitedVal}`;

            dataToImport.push({
              date: get(r, 'created date', 'date', 'created on', 'inquiry date', 'created at', 'date created', 'entry date'),
              clientName: name || 'Unknown Client',
              projectName: get(r, 'project name', 'project', 'title'),
              contactPersonName: get(r, 'contact person name', 'contact person', 'person name', 'person', 'poc', 'focal person'),
              designation: get(r, 'designation', 'title', 'role', 'position'),
              city: get(r, 'city', 'location'),
              address: get(r, 'address'),
              amount: cleanNumber(get(r, 'quotation amount', 'amount', 'quotation', 'estimate', 'price', 'total', 'value', 'proposal value')),
              quotationAmount: cleanNumber(get(r, 'quotation amount', 'amount', 'quotation', 'estimate', 'price', 'total', 'value', 'proposal value')),
              clientBudget: cleanNumber(get(r, 'client budget', 'budget', 'expected budget')),
              earnestMoney: cleanNumber(get(r, 'earnest money', 'earnest money value', 'earnest', 'security deposit')),
              documentStatus: get(r, 'doc submission status', 'document submission status', 'document status', 'doc status'),
              submissionDate: get(r, 'submission date', 'doc submission date', 'date of submission', 'document date', 'submission'),
              enlistedDate: get(r, 'enlisted on', 'enlisted date', 'date enlisted', 'enlistment date'),
              status: (() => {
                let rawStage = get(r, 'stage', 'status') || '';
                const lower = rawStage.toLowerCase();
                if (lower === 'site visited') return 'Quoted';
                if (lower === 'qualified') return 'Contacted';
                if (lower === 'negotiation') return 'Quoted';
                return rawStage;
              })(),
              notes: combinedNotes,
              nextCallDate: followUpVal || null,
              phone: phone,
              email: get(r, 'email', 'mail', 'email address', 'e-mail'),
              source: get(r, 'source of prequalification', 'prequalification source', 'source', 'channel', 'medium', 'lead source'),
              manager: get(r, 'manager', 'handling manager', 'assignee', 'assigned to', 'assignedto', 'sales', 'salesperson', 'employee', 'owner'),
              approvalStatus: get(r, 'approval status', 'approval', 'approvalstatus'),
              approvalRemarks: get(r, 'approval remarks', 'approval comments', 'approval note', 'approval remarks / comments'),
              inquiryDate: get(r, 'inquiry date', 'inquirydate', 'created at', 'created date', 'created on', 'created', 'prequalification date', 'date', 'entry date'),
              eventDate: get(r, 'event date', 'eventdate', 'function date'),
              eventType: get(r, 'function', 'event type', 'eventtype', 'function type', 'occasion'),
              guests: get(r, 'guests', 'pax', 'headcount', 'attendees'),
              venue: get(r, 'preferred venue', 'venue', 'location'),
              siteVisited: isSiteVisited,
              services: get(r, 'services', 'service', 'scope of work', 'scope', 'services required', 'scope of service', 'nature of work')
            });
          }
        });

        if (dataToImport.length === 0) {
          setImportProgress({
            ...INITIAL_PROGRESS,
            open: true,
            done: true,
            importError: `No valid ${importType} records found in the file.`,
          });
          return;
        }

        // Open modal and start import
        setImportProgress({
          open: true,
          total: dataToImport.length,
          current: 0,
          successCount: 0,
          failedRows: [],
          done: false,
          unmatchedSources: [],
        });

        const CHUNK_SIZE = 500;
        let successCount = 0;
        const failedRows = [];
        let processed = 0;

        for (let i = 0; i < dataToImport.length; i += CHUNK_SIZE) {
          const chunk = dataToImport.slice(i, i + CHUNK_SIZE);
          const chunkStartIndex = i;

          try {
            let result;
            if (importType === 'contacts' || importType === 'profiles') {
              result = await contactService.import(chunk);
            } else if (importType === 'users') {
              result = await userService.import(chunk);
            } else if (importType === 'tenders') {
              result = await tendersService.import(chunk);
            } else if (importType === 'contracts') {
              result = await contractsService.import(chunk);
            } else {
              result = await prequalificationService.import(chunk);
            }

            const chunkSuccess = result.data?.successCount ?? chunk.length;
            successCount += chunkSuccess;

            if (result.data?.errors?.length > 0) {
              result.data.errors.forEach((err, errIdx) => {
                const absRowNumber = headerIndex + 1 + chunkStartIndex + errIdx + 2;
                failedRows.push({
                  rowNumber: absRowNumber,
                  clientName: err.row || chunk[errIdx]?.clientName || chunk[errIdx]?.firstName || chunk[errIdx]?.name || '—',
                  error: err.error,
                });
              });
            }
          } catch (chunkErr) {
            chunk.forEach((item, ci) => {
              const absRowNumber = headerIndex + 1 + chunkStartIndex + ci + 2;
              failedRows.push({
                rowNumber: absRowNumber,
                clientName: item.clientName || item.firstName || item.name || 'Unknown',
                error: chunkErr.message,
              });
            });
          }

          processed += chunk.length;

          setImportProgress(prev => ({
            ...prev,
            current: processed,
            successCount,
            failedRows: [...failedRows],
          }));
        }

        setImportProgress(prev => ({
          ...prev,
          current: dataToImport.length,
          successCount,
          failedRows: [...failedRows],
          done: true,
        }));

        if (typeof onImportDone === 'function') {
          onImportDone();
        }

      } catch (error) {
        console.error('Import failed:', error);
        setImportProgress(prev => ({
          ...prev,
          done: true,
          failedRows: [...prev.failedRows, { rowNumber: '?', clientName: 'Unknown', error: error.message }],
        }));
      }
    };

    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          await processRows(rows);
        } catch (err) {
          console.error('Excel parse error:', err);
          setImportProgress({
            ...INITIAL_PROGRESS,
            open: true,
            done: true,
            importError: 'Failed to read Excel file: ' + err.message,
          });
        } finally {
          e.target.value = '';
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const rows = parseCSV(evt.target.result);
          await processRows(rows);
        } catch (err) {
          console.error('CSV parse error:', err);
          setImportProgress({
            ...INITIAL_PROGRESS,
            open: true,
            done: true,
            importError: 'Failed to read CSV file: ' + err.message,
          });
        } finally {
          e.target.value = '';
        }
      };
      reader.readAsText(file);
    }
  };

  return { importProgress, closeImportModal, handleFileUpload };
}
